import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useRetailerBatteries from '../../hooks/batteries/useRetailerBatteries';
import AsyncSection from '../Gateways/AsyncSection';
import HealthPill, { HEALTH_META, HEALTH_REPORTING_TBD } from './HealthPill';

const COLUMNS = [
    { key: 'name', label: 'Name' },
    { key: 'priceArea', label: 'Price area' },
    { key: 'members', label: 'Devices' },
    { key: 'health', label: 'Health' },
];

// Sort order for the health column: worst first, then the batteries nobody reports for.
const HEALTH_ORDER = { CRITICAL: 0, WARNING: 1, HEALTHY: 2 };

const sortValue = (battery, key) => {
    switch (key) {
        case 'name':
            return battery.name.toLowerCase();
        case 'priceArea':
            return battery.priceArea ?? '';
        case 'members':
            return battery.members.length;
        case 'health':
            // While every health cell reads "TBD", don't order rows by the real, hidden
            // value: a neutral key keeps the (stable) sort from moving anything.
            if (HEALTH_REPORTING_TBD) {
                return 0;
            }
            return battery.health === null ? 3 : HEALTH_ORDER[battery.health];
        default:
            return '';
    }
};

/**
 * Sort a list of batteries by one column.
 *
 * @param {Array<object>} batteries
 * @param {string} sortKey one of `COLUMNS[].key`
 * @param {1|-1} sortDir
 */
export const sortBatteries = (batteries, sortKey, sortDir) =>
    [...batteries].sort((a, b) => {
        const av = sortValue(a, sortKey);
        const bv = sortValue(b, sortKey);
        return (av > bv ? 1 : av < bv ? -1 : 0) * sortDir;
    });

/** Count of batteries per health state, `null` keyed as `none`. */
export const healthBreakdown = batteries => {
    const counts = { CRITICAL: 0, WARNING: 0, HEALTHY: 0, none: 0 };
    for (const battery of batteries) {
        counts[battery.health ?? 'none'] += 1;
    }
    return counts;
};

// The retailer's virtual batteries. Row → the battery page.
const BatteriesList = () => {
    const history = useHistory();
    const { batteries, isReady, error } = useRetailerBatteries();
    const [sortKey, setSortKey] = useState('name');
    const [sortDir, setSortDir] = useState(1);

    const sorted = useMemo(() => sortBatteries(batteries, sortKey, sortDir), [batteries, sortKey, sortDir]);
    const breakdown = useMemo(() => healthBreakdown(batteries), [batteries]);

    const toggleSort = key => {
        if (key === sortKey) {
            setSortDir(d => (d === 1 ? -1 : 1));
        } else {
            setSortKey(key);
            setSortDir(1);
        }
    };

    const openBattery = battery => {
        // Pass the row through router state so the page shows name/members instantly.
        history.push({ pathname: `/batteries/${battery.id}`, state: { battery } });
    };

    return (
        <div className="vb-list">
            <div className="vb-list__header">
                <div>
                    <h1 className="vb-list__title">Virtual batteries</h1>
                    <p className="vb-list__subtitle">Virtual batteries registered to your account.</p>
                </div>
                <div className="vb-list__tiles">
                    <div className="vb-tile">
                        <span className="vb-tile__label">Batteries</span>
                        <span className="vb-tile__value">{isReady && !error ? batteries.length : '—'}</span>
                    </div>
                    <div className="vb-tile">
                        <span className="vb-tile__label">Health breakdown</span>
                        <span className="vb-tile__breakdown">
                            {HEALTH_REPORTING_TBD
                                ? 'TBD'
                                : isReady && !error && batteries.length > 0
                                ? ['CRITICAL', 'WARNING', 'HEALTHY', 'none']
                                      .filter(key => breakdown[key] > 0)
                                      .map(key => (
                                          <span key={key} className={`vb-tile__breakdown-item vb-health--${HEALTH_META[key].className}`}>
                                              <span className="vb-health__dot" />
                                              {HEALTH_META[key].label} {breakdown[key]}
                                          </span>
                                      ))
                                : '—'}
                        </span>
                    </div>
                </div>
            </div>

            <div className="card-wrapper vb-list__card">
                <AsyncSection
                    isReady={isReady}
                    error={error}
                    isEmpty={batteries.length === 0}
                    errorText="Could not load virtual batteries."
                    emptyText="No virtual batteries are registered to your account yet."
                >
                    <table className="vb-table">
                        <thead>
                            <tr>
                                {COLUMNS.map(col => (
                                    <th key={col.key}>
                                        <button type="button" className="vb-table__sort" onClick={() => toggleSort(col.key)}>
                                            {col.label}
                                            {sortKey === col.key ? (
                                                <i className={`fa-solid ${sortDir === 1 ? 'fa-arrow-up' : 'fa-arrow-down'}`} />
                                            ) : null}
                                        </button>
                                    </th>
                                ))}
                                <th aria-label="Open" />
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map(battery => (
                                <tr
                                    key={battery.id}
                                    className="vb-table__row"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => openBattery(battery)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            openBattery(battery);
                                        }
                                    }}
                                >
                                    <td>
                                        <span className="vb-table__name">
                                            <img src="/assets/icons/battery.svg" alt="" className="vb-table__icon" />
                                            {battery.name}
                                        </span>
                                    </td>
                                    <td className="vb-table__mono">{battery.priceArea ?? '—'}</td>
                                    <td>{battery.members.length}</td>
                                    <td>
                                        <HealthPill status={battery.health} />
                                    </td>
                                    <td className="vb-table__open">
                                        <i className="fa-solid fa-arrow-right" />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </AsyncSection>
            </div>
        </div>
    );
};

export default BatteriesList;
